import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { camelCase } from 'change-case';
import { map, pipe, uniq } from 'ramda';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';

import { DropArea } from './UploadDropZone.styled';

const extractValidations = ({ minSize, maxSize, accept, isMultiFileUpload }: UploadDropZoneRestrictionProps) => {
  return {
    minSize,
    maxSize,
    supportedFileTypeList: Object.values(accept || {}).reduce((types: any[], current) => types.concat(current), []),
    maxFiles: isMultiFileUpload ? undefined : 1,
  };
};

export interface UploadAcceptProps {
  [key: string]: string[];
}

interface UploadDropZoneRestrictionProps {
  isMultiFileUpload?: boolean;
  minSize?: number;
  maxSize?: number;
  accept?: UploadAcceptProps;
}
interface FileUpload {
  chatSessionId: string;
  file: File;
  fileId: string;
  name: string;
}

interface UploadDropZoneCallbacks {
  onFileAdded?: (fileName: string) => void;
  onFileUploadStart?: () => void;
  onFileUploadEnd?: (files: FileUpload[], errors: string[]) => void;
  onDropErrors?: (messages: string[]) => void;
  onDrop: (files: File[]) => void;
}

export interface UploadDropZoneProps extends UploadDropZoneRestrictionProps, UploadDropZoneCallbacks {
  disabled?: boolean;
  label?: string;
  buttonLabel?: string;
}

export const UploadDropZone = ({
  isMultiFileUpload,
  onFileAdded,
  onFileUploadStart,
  onFileUploadEnd,
  onDropErrors,
  onDrop,
  ...dropZoneProps
}: UploadDropZoneProps) => {
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'FileUpload' });

  const { minSize, maxSize, maxFiles, supportedFileTypeList } = extractValidations({
    ...dropZoneProps,
    isMultiFileUpload,
  });

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    ...dropZoneProps,
    multiple: isMultiFileUpload,
    maxFiles: maxFiles,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onDropRejected(fileRejections) {
      const errorMessages = pipe(
        map((rejection: FileRejection) => {
          const code = camelCase(rejection.errors[0].code);
          const fileName = rejection.file.name;
          const supportedFileTypes = supportedFileTypeList.join(', ');
          return t(`errors.${code}`, { fileName, maxSize, minSize, supportedFileTypes, maxFiles });
        }),
        uniq,
      )(fileRejections);
      onDropErrors?.(errorMessages);
    },
  });

  const dropAreaProps = { ...getRootProps(), isDragReject, isDragActive, isDragAccept };
  return (
    <DropArea {...dropAreaProps}>
      <Stack
        direction="row"
        spacing={3}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Button
          disabled={dropZoneProps.disabled}
          variant="contained"
          color="primary"
          endIcon={<UploadFileIcon />}
          onClick={open}
        >
          {dropZoneProps.buttonLabel ?? t('button')}
        </Button>
        <input {...getInputProps()} />
        <Stack>
          <Typography variant="body2" color="textPrimary" gutterBottom={false}>
            {dropZoneProps.label ?? t('label')}
          </Typography>
        </Stack>
      </Stack>
    </DropArea>
  );
};
